import { createGlobalStyle } from 'styled-components';
import { colors as colorsDefault } from './theme';

const base = {
  gridSize: 12,
  gutterWidth: 1,
  outerMargin: 2,
};

const fontFamily = 'Barlow Condensed, sans-serif';
const fontFamilyMedium = 'Barlow Condensed, sans-serif';
const fontFamilySecondary = 'Barlow Condensed, serif';
const fontFamilySecondaryMedium = 'Barlow Condensed, serif';
const fontFamilyTertiary = 'Barlow Condensed, serif';
const fontFamilyTertiaryMedium = 'Barlow Condensed, serif';

const colors = {
  ...colorsDefault,
  biscay: '#02BBEC',
  shiny: '#3accf3',
  golden: '#C82656',
  merino: '#faf9f7',
  cafeRoyale: '#C82656',
  keppel: '#02BBEC',
  keppel40: 'rgba(58,204,243,0.4)',
  goldenButton: '#939394',
  ferra: '#e1e1e4',
  vinrouge: '#a2a2a7',
  grandis: '#02BBEC',
  grayDark: '#22222b',
  athensGray: 'rgba(58,204,243,0.07)',
  alabaster: 'rgb(247,247,247)',
  footerBgColor: '#2a2a2a',
  textFooterColor: '#fff',
  textFooterColorLight: 'rgba(255, 255, 255, 0.36)',
};

const secondaryColors = {
  blue10: '#ade7ff',
  blue20: '#61bcff',
  darkGrey: '#AEADAD',
};

const breakpoints = {
  xs: '0px',
  ms: '360px',
  sm: '600px',
  md: '960px',
  mdLegacy: '1199px',
  lg: '1280px',
  xl: '1920px',
};

interface StyleClosetTheme {
  base: {};
  breakpoints: {};
  fontFamily: string;
  fontFamilyMedium: string;
  fontFamilySecondary: string;
  fontFamilySecondaryMedium: string;
  fontFamilyTertiary: string;
  fontFamilyTertiaryMedium: string;
  colors: { [key in keyof typeof colors]: string };
  secondaryColors: { [key in keyof typeof secondaryColors]: string };
}

const theme: StyleClosetTheme = {
  base,
  breakpoints,
  fontFamily,
  fontFamilyMedium,
  fontFamilySecondary,
  fontFamilySecondaryMedium,
  fontFamilyTertiary,
  fontFamilyTertiaryMedium,
  colors,
  secondaryColors,
};

export const GlobalStyle = createGlobalStyle`
  *, :after, :before {
      box-sizing: border-box;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
  }
  html {
    &, input, button, select, textarea, h1, h2, h3, h4, h5, h6, .MuiTab-root{
      font-family: ${fontFamily};
    }
    hr{
      margin-top: 10px;
      margin-bottom: 10px;
      border: 0;
      border-top: 1px solid ${colors.silver};
    }
    header {
      margin-top: 0;
      width: 100%;
      top: 0;
      position: fixed;
      z-index: 15;
    }
    body{
      font-size: 1rem;
      line-height: 1.5;
      margin: 0;
      &.disableScroll {
        overflow: hidden;
      }
      .slick-track {
        will-change: transform;
      }
      .slick-slide{
        > div{
          > div{
            outline: none!important;
          }
        }
      }
    }
    a {
      text-decoration: none;
      color: inherit;
    }
    button {
      cursor: pointer;
    }
    input {
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
    }
    svg:not(:root) {
      overflow: hidden;
    }
    p {
      margin: 0 0 10px;
    }

    ul {
      margin-top: 0;
      margin-bottom: 10px;
    }
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-weight: 500;
      line-height: 1.1;
    }
    h1,
    h2,
    h3 {
      margin-top: 20px;
      margin-bottom: 10px;
    }
    h4,
    h5,
    h6 {
      margin-top: 10px;
      margin-bottom: 10px;
    }
    h1 {
      font-size: 36px;
    }
    h2 {
      font-size: 30px;
    }
    h3 {
      font-size: 24px;
    }
    h4 {
      font-size: 18px;
    }
    h5 {
      font-size: 14px;
    }
    h6 {
      font-size: 12px;
    }
    input,
    button,
    select,
    textarea {
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
    }
  }
  #campaign,
  #home,
  #destination,
  #destinationInfo,
  #destinationLandings {
    background-color: white;
    min-height: 100vh;
  }
  #checkout {
    padding: 85px 16px 32px 16px;
    @media (max-width: 479px) {
      padding: 40px 0 34px 0;
    }
  }
  .zindexHeader{
    z-index: 1600;
  }
  .zindexHeaderUnset{
    z-index: 15;
  }
  .lazyHeroPlaceholder {
    filter: blur(1rem);
    height: 100%!important;
    width: 100%!important;
  }
  .lazyPlaceholder {
    filter: blur(1rem);
    height: calc(100% + 2rem)!important;
    width: calc(100% + 2rem)!important;
    margin-left: -1rem;
    margin-top: -1rem;
  }

  /* legacy ICOMOON icons */
  @font-face {
    font-family: 'icomoon';
    src: url("data:application/x-font-ttf;charset=utf-8;base64,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") format('truetype');
    font-weight: normal;
    font-style: normal;
    font-display: block;
  }
`;

export { theme };
